// src/Components/DeviceTrendChart.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../utils/api';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
  Filler,
} from 'chart.js';
import '../css/DeviceTrendChart.css';   // ✅ 추가

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
  Filler
);

export default function DeviceTrendChart({ deviceId, initialStart, initialEnd, className = '' }) {
  /* ===== 네온 팔레트 ===== */
  const C = {
    bg: 'linear-gradient(180deg, rgba(0,10,25,.92), rgba(0,16,36,.98))',
    border: 'rgba(0,255,255,.22)',
    shadow: '0 12px 34px rgba(0,0,0,.55), 0 0 22px rgba(0,240,255,.08)',
    text: '#dff9ff',
    textDim: '#a9f0ff',
    axis: '#baf6ff',
    grid: 'rgba(110,231,255,.22)',
    danger: '#ff8b60',
    lineRed: '#ff6b6b',
    lineBlue: '#22d3ee',
    lineGreen: '#34d399',
  };

  // helpers
  function toYmd(d) { const y = d.getFullYear(); const m = `${d.getMonth() + 1}`.padStart(2, '0'); const day = `${d.getDate()}`.padStart(2, '0'); return `${y}-${m}-${day}`; }
  function addDays(d, diff) { const nd = new Date(d); nd.setDate(nd.getDate() + diff); return nd; }
  function round2(v) { return Math.round((v + Number.EPSILON) * 100) / 100; }
  function pad2(n) { return String(n).padStart(2, '0'); }

  const today = useMemo(() => toYmd(new Date()), []);
  const sevenDaysAgo = useMemo(() => toYmd(addDays(new Date(), -6)), []);
  const [start, setStart] = useState(initialStart || sevenDaysAgo);
  const [end, setEnd] = useState(initialEnd || today);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const tempCanvasRef = useRef(null);
  const comboCanvasRef = useRef(null);
  const chartRefs = useRef({ temp: null, combo: null });

  const sameDay = start && end && start === end;
  const disabled = !deviceId || !start || !end || start > end;

  const fetchData = async () => {
    if (disabled) return;
    setLoading(true);
    setErr('');
    try {
      let list = [];
      if (sameDay) {
        // 시간대별
        try {
          const r1 = await api.get(`/${encodeURIComponent(deviceId)}/hourly-avg`, { params: { date: start } });
          list = Array.isArray(r1.data) ? r1.data : [];
          list = list
            .filter((d) => d && (d.hour !== undefined && d.hour !== null))
            .sort((a, b) => Number(a.hour) - Number(b.hour));
        } catch {
          const r2 = await api.get(`/devices/${encodeURIComponent(deviceId)}/daily-avg`, { params: { start, end } });
          list = Array.isArray(r2.data) ? r2.data : [];
        }
      } else {
        const r = await api.get(`/devices/${encodeURIComponent(deviceId)}/daily-avg`, { params: { start, end } });
        list = Array.isArray(r.data) ? r.data : [];
      }
      setData(list);
    } catch (e) {
      console.error(e);
      setErr('데이터 조회에 실패했습니다.');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [deviceId, start, end]);

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: C.text },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0,16,36,.96)',
        borderWidth: 1,
        borderColor: C.border,
        titleColor: C.text,
        bodyColor: C.text,
        displayColors: true,
      },
    },
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        ticks: { color: C.axis, maxRotation: 0 },
        grid: { color: C.grid },
        title: { display: true, text: '', color: C.textDim },
      },
      y: {
        ticks: { color: C.axis },
        grid: { color: C.grid },
        title: { display: true, text: '', color: C.textDim },
      },
    },
  };

  useEffect(() => {
    const destroyAll = () => {
      if (chartRefs.current.temp) { chartRefs.current.temp.destroy(); chartRefs.current.temp = null; }
      if (chartRefs.current.combo) { chartRefs.current.combo.destroy(); chartRefs.current.combo = null; }
    };
    destroyAll();
    if (!data || data.length === 0) return;

    const labels = sameDay ? data.map((d) => `${pad2(d.hour ?? 0)}:00`) : data.map((d) => d.day);
    const temperature = data.map((d) => round2(d.temperature ?? 0));
    const mss = data.map((d) => round2(d.movingStandardSmoke ?? 0));
    const sav = data.map((d) => round2(d.smokeAvgValue ?? 0));

    if (tempCanvasRef.current) {
      chartRefs.current.temp = new Chart(tempCanvasRef.current.getContext('2d'), {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: '온도',
            data: temperature,
            tension: 0.28,
            pointRadius: 2,
            borderWidth: 2,
            borderColor: C.lineRed,
            backgroundColor: 'rgba(255,107,107,.15)',
            fill: true,
          }],
        },
        options: {
          ...commonOptions,
          scales: {
            ...commonOptions.scales,
            x: { ...commonOptions.scales.x, title: { display: true, text: sameDay ? '시간' : '날짜', color: C.textDim } },
            y: { ...commonOptions.scales.y, title: { display: true, text: '온도', color: C.textDim } },
          },
        },
      });
    }

    if (comboCanvasRef.current) {
      chartRefs.current.combo = new Chart(comboCanvasRef.current.getContext('2d'), {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: '연기 농도 기준값',
              data: mss,
              tension: 0.28,
              pointRadius: 2,
              borderWidth: 2,
              borderColor: C.lineBlue,
              backgroundColor: 'rgba(34,211,238,.14)',
              fill: true,
            },
            {
              label: '연기 농도값',
              data: sav,
              tension: 0.28,
              pointRadius: 2,
              borderWidth: 2,
              borderColor: C.lineGreen,
              backgroundColor: 'rgba(52,211,153,.14)',
              fill: true,
            },
          ],
        },
        options: {
          ...commonOptions,
          scales: {
            ...commonOptions.scales,
            x: { ...commonOptions.scales.x, title: { display: true, text: sameDay ? '시간' : '날짜', color: C.textDim } },
            y: { ...commonOptions.scales.y, title: { display: true, text: '연기 값', color: C.textDim } },
          },
        },
      });
    }

    return () => destroyAll();
  }, [data, sameDay]); // eslint-disable-line react-hooks/exhaustive-deps

  // 카드 기본 스타일(테마)
  const cardStyle = {
    background: C.bg,
    borderRadius: 14,
    padding: '16px 18px',
    border: `1px solid ${C.border}`,
    boxShadow: C.shadow,
    color: C.text,
  };

  const titleStyle = {
    margin: 0,
    fontSize: 18,
    color: C.text,
    textShadow: '0 0 8px rgba(0,255,255,.28)',
  };

  const subTitleStyle = {
    margin: '0 0 6px 0',
    fontSize: 14,
    color: C.textDim,
  };

  const labelStyle = { fontSize: 12, color: C.textDim };

  const inputStyle = {
    height: 34,
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    padding: '0 10px',
    background: 'rgba(0,18,36,.65)',
    color: C.text,
    outline: 'none',
  };

  const buttonStyle = {
    height: 34,
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    padding: '0 12px',
    background: 'rgba(0,25,55,.65)',
    color: C.text,
    cursor: 'pointer',
    fontWeight: 700,
    transition: 'transform .12s ease, box-shadow .2s ease, background .2s ease, border-color .2s ease',
  };

  const loadingStyle = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textDim };
  const errorStyle = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.danger, fontWeight: 700 };
  const emptyStyle = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textDim };
  const footerStyle = { marginTop: 10, color: C.textDim, fontSize: 12 };

  const onBtnEnter = (e) => {
    e.currentTarget.style.background = 'rgba(0,32,72,.82)';
    e.currentTarget.style.borderColor = 'rgba(0,255,255,.6)';
    e.currentTarget.style.boxShadow = '0 0 12px rgba(0,255,255,.35)';
    e.currentTarget.style.transform = 'translateY(-1px)';
  };
  const onBtnLeave = (e) => {
    e.currentTarget.style.background = 'rgba(0,25,55,.65)';
    e.currentTarget.style.borderColor = C.border;
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.transform = 'none';
  };
  const onInputFocus = (e) => { e.target.style.boxShadow = '0 0 12px rgba(0,255,255,.28)'; };
  const onInputBlur = (e) => { e.target.style.boxShadow = 'none'; };

  return (
    <div className={`device-trend-card ${className}`} style={cardStyle}>
      {/* 헤더 */}
      <div className="device-trend-header">
        <div className="device-trend-header__info">
          <h2 className="device-trend-title" style={titleStyle}>
            {sameDay ? '시간대 평균' : '일자별 평균'}
          </h2>
          <div className="device-trend-device">
            <span>Device:&nbsp;</span>
            <strong>{deviceId || '-'}</strong>
          </div>
        </div>

        <div className="device-trend-header__controls">
          <div className="trend-field">
            <label className="trend-field-label" style={labelStyle}>시작일</label>
            <input
              type="date"
              value={start}
              max={end || undefined}
              onChange={(e) => setStart(e.target.value)}
              style={inputStyle}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
            />
          </div>
          <div className="trend-field">
            <label className="trend-field-label" style={labelStyle}>종료일</label>
            <input
              type="date"
              value={end}
              min={start || undefined}
              onChange={(e) => setEnd(e.target.value)}
              style={inputStyle}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
            />
          </div>
          <button
            className="trend-refresh-btn"
            onClick={fetchData}
            disabled={disabled || loading}
            style={buttonStyle}
            onMouseEnter={onBtnEnter}
            onMouseLeave={onBtnLeave}
            title="다시 조회"
          >
            조회
          </button>
        </div>
      </div>

      {/* 차트 두 개 */}
      <div className="device-trend-body">
        <section className="trend-section trend-section--temp">
          <h3 className="trend-section-title" style={subTitleStyle}>온도</h3>
          {loading ? (
            <div style={loadingStyle}>⏳ 불러오는 중…</div>
          ) : err ? (
            <div style={errorStyle}>{err}</div>
          ) : data.length === 0 ? (
            <div style={emptyStyle}>표시할 데이터가 없습니다.</div>
          ) : (
            <canvas ref={tempCanvasRef} />
          )}
        </section>

        <section className="trend-section trend-section--smoke">
          <h3 className="trend-section-title" style={subTitleStyle}>연기 값</h3>
          {loading ? (
            <div style={loadingStyle}>⏳ 불러오는 중…</div>
          ) : err ? (
            <div style={errorStyle}>{err}</div>
          ) : data.length === 0 ? (
            <div style={emptyStyle}>표시할 데이터가 없습니다.</div>
          ) : (
            <canvas ref={comboCanvasRef} />
          )}
        </section>
      </div>

      <div className="device-trend-footer" style={footerStyle}>
        <small>
          평균은 선택한 기간에 따라 {sameDay ? '시간대별' : '일자별'} 데이터 기준으로 계산됩니다. (KST)
        </small>
      </div>
    </div>
  );
}
